import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Property } from '@/types/property';
import { usePropertyMutations } from '@/hooks/usePropertyMutations';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';
import { useEffect } from 'react';

interface PropertyEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property | null;
}

const formSchema = z.object({
  description: z.string().optional(),
  videoUrl: z.string().url('Debe ser una URL válida').optional().or(z.literal('')),
  code: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const PropertyEntryModal = ({ isOpen, onClose, property }: PropertyEntryModalProps) => {
  const { updateProperty } = usePropertyMutations();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: '',
      videoUrl: '',
      code: '',
    },
  });

  useEffect(() => {
    if (isOpen && property) {
      form.reset({
        description: property.entryDescription || '',
        videoUrl: property.entryVideoUrl || '',
        code: property.securityCode || '',
      });
    }
  }, [property, isOpen, form]);

  const onSubmit = async (data: FormData) => {
    if (!property) return;

    try {
      await updateProperty.mutateAsync({
        id: property.id,
        entryDescription: data.description || null,
        entryVideoUrl: data.videoUrl || null,
        securityCode: data.code || null,
      });
      onClose();
    } catch (error) {
      console.error('Error saving property entry info:', error);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Instrucciones de Entrada"
      description={property?.shortName || property?.title}
      form={form}
      onSubmit={onSubmit}
      submitLabel="Guardar"
      isLoading={updateProperty.isPending}
      maxWidth="max-w-[550px]"
    >
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descripción</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Ej: El ingreso es autónomo con caja de seguridad"
                  {...field}
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="videoUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL Video</FormLabel>
              <FormControl>
                <Input
                  placeholder="https://youtube.com/..."
                  {...field}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                URL de YouTube con instrucciones de cómo abrir la puerta
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Código</FormLabel>
              <FormControl>
                <Input
                  placeholder="Código de la caja de seguridad"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default PropertyEntryModal;
